import { useEffect, useRef, useState } from "react";
import { SectionNav } from "@/components/road/SectionNav.tsx";
import { useCatalog } from "@/lib/catalog/context.tsx";
import { activeFilterCount } from "@/lib/filters/apply.ts";
import { useFilteredRoad } from "@/lib/filters/context.tsx";
import { useMediaQuery } from "@/lib/useMediaQuery.ts";
import { useFilters } from "@/state/filters.ts";
import { formatMinutes } from "../entry/RuntimeChip.tsx";
import { CharacterFilter } from "./CharacterFilter.tsx";
import styles from "./ControlPanel.module.css";
import { FilterFooter } from "./FilterFooter.tsx";
import { MediaToggles } from "./MediaToggles.tsx";
import { QualityGate } from "./QualityGate.tsx";
import { RuntimeFilter } from "./RuntimeFilter.tsx";
import { ThreadFilter } from "./ThreadFilter.tsx";
import { TierSlider } from "./TierSlider.tsx";
import { TrackPresets } from "./TrackPresets.tsx";

/** The one breakpoint that isn't purely presentational — see below. */
const DESKTOP = "(min-width: 1024px)";

function PanelFields() {
  return (
    <div className={styles.fields}>
      <TrackPresets />
      <TierSlider />
      <QualityGate />
      <CharacterFilter />
      <ThreadFilter />
      <RuntimeFilter />
      <MediaToggles />
    </div>
  );
}

/**
 * The filters, as a persistent sidebar at ≥1024px and a modal bottom sheet
 * below it.
 *
 * The two are different enough to be different DOM: a sidebar wants to be a
 * plain landmark that's always there, and a sheet wants focus trapping,
 * Escape-to-close and an inert page behind it — which is exactly what
 * `<dialog>.showModal()` gives for free, and what a CSS-only sheet would have
 * to reimplement by hand and get subtly wrong. Hence the single JS media
 * query; everything else about the layout is CSS.
 *
 * A modal sheet does mean you can't watch the road change behind it on a
 * phone, so the sheet's own footer carries the running "N of 81 stops" tally
 * and the dock underneath it repeats the tally when closed.
 */
export function ControlPanel() {
  const isDesktop = useMediaQuery(DESKTOP);
  const catalog = useCatalog();
  const filters = useFilters();
  const { entries, minutes } = useFilteredRoad();
  const [open, setOpen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // Crossing up to the sidebar layout unmounts the sheet; make sure it doesn't
  // come back open when the window narrows again.
  useEffect(() => {
    if (isDesktop) setOpen(false);
  }, [isDesktop]);

  const active = activeFilterCount(filters);
  const tally = `${entries.length} of ${catalog.totals.entryCount} stops · ${formatMinutes(minutes)}`;

  if (isDesktop) {
    return (
      <aside className={styles.sidebar} aria-label="Filters">
        <div className={styles.sidebarInner}>
          <PanelFields />
          <FilterFooter />
          <SectionNav />
        </div>
      </aside>
    );
  }

  return (
    <>
      <div className={styles.dock}>
        <button
          type="button"
          className={styles.dockButton}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          Filters
          {active > 0 && <span className={styles.dockBadge}>{active}</span>}
        </button>
        <p className={styles.dockTally}>{tally}</p>
      </div>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: the click handler is backdrop-dismissal only; a modal <dialog> already closes on Escape natively, which IS the keyboard equivalent, and every control inside is separately focusable. */}
      <dialog
        ref={dialogRef}
        className={styles.sheet}
        aria-label="Filters"
        onClose={() => setOpen(false)}
        onClick={(event) => {
          // Clicks land on the <dialog> itself only when they hit the backdrop.
          if (event.target === dialogRef.current) setOpen(false);
        }}
      >
        <div className={styles.sheetInner}>
          <div className={styles.sheetHead}>
            <h2 className={styles.sheetTitle}>Filters</h2>
            <button type="button" className={styles.sheetDone} onClick={() => setOpen(false)}>
              Done
            </button>
          </div>
          <PanelFields />
          <FilterFooter />
          <SectionNav />
        </div>
      </dialog>
    </>
  );
}
